import { useLocation, useNavigate } from 'react-router-dom';

import { BrandliveEvent, IEventTag } from '@/types/projects.types';
import toastTrigger from '@ui/toast/toast-trigger';
import { handleAddNewSession } from '@/utils/handle-add-new-session';
import { getDefaultLanguage } from '@/utils/get-default-language';
import { setWorkingProject } from '@/store/slices/project';
import CreateSpeakers from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/helpers/cast-and-crew';
import { GetProject } from '@/connection/projects/get-project';
import { BLAdmin } from '@/types/profile.types';
import { useAppDispatch } from '@/store/hooks';
import { BreakoutRoom, Session } from '@/types/session.types';
import { Speaker } from '@/types/speakers.types';
import { ESessionFeatures } from '@/types/session-features.types';
import { GetDefaultQuestionPrompt } from '@/utils/modules/get-default-question-prompt';
import { CreateQuestionPrompt, ModuleGroupingTypes, PageModuleType } from '@/types/module.types';
import { updateTranslateKey } from '@/utils/update-translation-key';
import { CreateQuestionModule } from '@/connection/modules/create-question-module';
import { CreatePageModule } from '@/connection/modules/create-page-module';
import { Templates } from '@/types/template.types';
import { UpdateSession } from '@/connection/sessions/update-session';
import { LanguagesAbbr } from '@/types/translation.types';
import { TBroadcastTypePickerOptions } from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/broadcast-type-picker/broadcast-type-picker.types';
import { EToastTypes } from '@ui/toast/toast.types';

export const handleCreateNewSession = async ({
  project,
  user,
  channel,
  dispatch,
  userSelections,
  navigate,
  location,
  paramsLanguage = 'en',
  handleCloseModal,
  navigateTo,
}: {
  project: BrandliveEvent | null;
  user: BLAdmin | null;
  channel: number | null;
  dispatch: ReturnType<typeof useAppDispatch>;
  userSelections: {
    name: string;
    description: string;
    times: { timestamp: number | null; end_timestamp: number | null };
    imageUrl: string | null;
    selectedBroadcastType: TBroadcastTypePickerOptions;
    categories: string[];
    tags: IEventTag[];
    speakers: Speaker[];
    features: ESessionFeatures[];
    languages: string[];
    defaultLanguage: string;
    firesidesHosts?: string[];
    breakoutRooms?: BreakoutRoom[];
  };
  navigate: ReturnType<typeof useNavigate>;
  location: ReturnType<typeof useLocation>;
  paramsLanguage?: string;
  handleCloseModal?: () => void;
  navigateTo?: string;
}) => {
  try {
    if (!project || !user || !channel) {
      throw new Error('Project, user and channel are required');
    }

    const defaultLanguage = (userSelections.defaultLanguage || getDefaultLanguage(project)) as LanguagesAbbr;

    // create new session
    // then add speakers and tags after the fact
    let session = await handleAddNewSession({
      saveAsynchronously: true,
      project,
      user,
      channel,
      defaultLanguage,
      dispatch,
      title: userSelections.name,
      description: userSelections.description,
      startTimestamp: userSelections.times.timestamp,
      endTimestamp: userSelections.times.end_timestamp,
      image: userSelections.imageUrl,
      selectedBroadcastType: userSelections.selectedBroadcastType,
      tracks: userSelections.categories,
      tags: userSelections.tags,
      features: userSelections.features,
      languages: userSelections.languages,
      firesides_hosts: userSelections.firesidesHosts || [],
      breakoutRooms: userSelections.breakoutRooms || [],
    });

    if (!session) {
      throw new Error('Error creating session');
    }

    if (userSelections.features.includes(ESessionFeatures.QA)) {
      const updatedSession = await addQuestionModuleToNewSession({
        project,
        session,
        channel,
        defaultLanguage,
        paramsLanguage,
      });
      if (updatedSession) {
        session = updatedSession;
      }
    }

    const speakers = userSelections.speakers;
    if (speakers.length) {
      await addSpeakersToNewSession({ project, session, speakers, dispatch });
    }

    // need to reload project after saving to ensure we have the latest data
    try {
      const reloadedProject = await GetProject(project.uuid);
      if (!reloadedProject || 'error' in reloadedProject) {
        console.error('Error reloading project', reloadedProject);
        throw new Error('Error saving speakers');
      }
      dispatch(setWorkingProject(reloadedProject));
    } catch (error) {
      console.error('Error reloading project', error);
      // force a page reload if we fail to fetch the project so it pulls in the latest data
      window.location.reload();
    }

    // Simply calling navigate will suffice, however the transition's not that smooth, so
    // we will call "close modal" first, then wrap in a timeout before navigating so the modal animates closed first
    handleCloseModal?.();
    setTimeout(() => {
      navigate(navigateTo || `${location.pathname}/session/${session.uuid}`, { viewTransition: true });
      /* note that 400ms is the modal close duration */
    }, 400);
  } catch (error) {
    console.error('Error saving session', error);
    toastTrigger('Error creating the session', {
      type: EToastTypes.error,
      description: 'Please try again',
    });
    return Promise.reject(error);
  }
};

const addQuestionModuleToNewSession = async ({
  project,
  session,
  channel,
  defaultLanguage,
  paramsLanguage,
}: {
  project: BrandliveEvent;
  session: Session;
  channel: number;
  defaultLanguage: LanguagesAbbr;
  paramsLanguage: string;
}): Promise<Session | null> => {
  try {
    // we need to reload the project after saving the session
    // because the project will need to have the new session data with it
    // this call will also update the store with the new project data
    // so we can use the updated project data to save the speakers
    const reloadedProject = await GetProject(project.uuid);
    if (!reloadedProject || 'error' in reloadedProject) {
      console.error('Error reloading project', reloadedProject);
      throw new Error('Error saving project');
    }
    const reloadedSession = reloadedProject?.sessions.find((s) => s.uuid === session?.uuid) || session;
    const newPageModule = await CreatePageModule({
      type: PageModuleType.questions,
      eventName: project.name,
      template: Templates.Limelight,
      languages: reloadedSession.languages,
      baseLanguage: reloadedSession.default_language,
    });

    if (!newPageModule) return null;

    const baseQuestionPrompt: CreateQuestionPrompt = {
      ...GetDefaultQuestionPrompt(),
      title: updateTranslateKey({
        translateString: 'Ask question',
        input: 'Ask question',
        baseLanguage: defaultLanguage,
        language: paramsLanguage,
      }),
    };
    const { pageModule, questionPrompt } = await CreateQuestionModule({
      questionPrompt: { ...baseQuestionPrompt, channel },
      pageModule: Number(newPageModule.id),
      baseLanguage: session.default_language,
      languages: session.languages,
    });

    const module = { ...pageModule, modules: [questionPrompt] };
    // update session modules
    if (!session.modules || !session.modules.find((m) => m.id === module.id)) {
      session.modules = [...(session.modules || []), module];
    } else {
      session.modules = session.modules.map((m) => {
        if (m.id === module.id) {
          return {
            ...m,
            modules: [...(m.modules || []), module],
          };
        }
        return m;
      });
    }

    // add newPageModule id to session engage group
    session.module_grouping = (session.module_grouping || []).map((group) => {
      if (group.type !== ModuleGroupingTypes.Engage || !newPageModule.id) {
        return group;
      }
      const newModules = Array.from(new Set([...(group.modules || []), newPageModule.id]));
      return {
        ...group,
        modules: newModules,
      };
    });

    return UpdateSession(session);
  } catch (error) {
    console.error('Error creating question module', error);
    toastTrigger('Error creating Q&A module', {
      type: EToastTypes.error,
      autoClose: 7000,
      description:
        'Session successfully created, but the Q&A failed to save. You can edit your Q&A module directly inside the session',
    });
    return null;
  }
};

const addSpeakersToNewSession = async ({
  project,
  session,
  speakers,
  dispatch,
}: {
  project: BrandliveEvent;
  session: Session;
  speakers: Speaker[];
  dispatch: ReturnType<typeof useAppDispatch>;
}) => {
  try {
    // we need to reload the project after saving the session
    // because the project will need to have the new session data with it
    // this call will also update the store with the new project data
    // so we can use the updated project data to save the speakers
    const reloadedProject = await GetProject(project.uuid);
    if (!reloadedProject || 'error' in reloadedProject) {
      console.error('Error reloading project', reloadedProject);
      throw new Error('Error saving project');
    }
    const reloadedSession = reloadedProject?.sessions.find((s) => s.uuid === session?.uuid) || session;
    await CreateSpeakers({
      saveAsynchronously: true,
      speakers,
      dispatch,
      project: reloadedProject,
      session: reloadedSession,
    });
  } catch (error) {
    console.error('Error saving speakers', error);
    toastTrigger('Error adding speakers', {
      type: EToastTypes.error,
      autoClose: 7000,
      description:
        'Session successfully created, but speakers failed to save. You can edit your speakers directly inside the session',
    });
  }
};
